import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';

import {
    DynamicComponentDirective,
    ElementRepositoryService,
    SwipeDirection,
    SwipeDirective,
    TimerService,
    VanillaElements,
    WINDOW,
} from '@frontend/vanilla/core';
import {
    AccountMenuDataService,
    AccountMenuTasksService,
    BOTTOM_DRAWER_HEIGHT,
    DrawerPosition,
    MenuRoute,
} from '@frontend/vanilla/shared/account-menu';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { AccountMenuDrawerService } from '../account-menu-drawer.service';
import { AccountMenuItemBase } from '../account-menu-item-base';
import { AccountMenuScrollService } from '../account-menu-scroll.service';

@Component({
    imports: [CommonModule, DynamicComponentDirective, SwipeDirective],
    selector: 'vn-am-icon-menu',
    templateUrl: 'icon-menu.html',
    styleUrls: ['../../../../../../themepark/themes/whitelabel/components/animated-arrow-icon/styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class IconMenuComponent extends AccountMenuItemBase implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('linkContainer') linkContainer: ElementRef;
    private startY: number;
    private scrollTop: number = 0;
    private scrollMode: boolean = false;
    private readonly unsubscribe = new Subject();
    private readonly SWIPE_THRESHOLD = 25;

    accountMenuDrawerService = inject(AccountMenuDrawerService);
    readonly #window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);
    private readonly accountMenuTasksService = inject(AccountMenuTasksService);
    private readonly accountMenuDataService = inject(AccountMenuDataService);
    private readonly accountMenuScrollService = inject(AccountMenuScrollService);
    private readonly elementRepositoryService = inject(ElementRepositoryService);
    private readonly timerService = inject(TimerService);

    constructor() {
        super();
    }

    get templateVersion(): number {
        if (this.version === 1) {
            return 1;
        }

        if (this.version === 3 && this.accountMenuDataService.routerMode) {
            return 3;
        }

        return 2;
    }

    private get scrollOffset(): number {
        return this.scrollMode ? 25 : 0;
    }

    ngOnInit() {
        this.accountMenuScrollService.scroll.subscribe((scrollTop: number) => this.onScroll(scrollTop));
    }

    ngAfterViewInit() {
        if (this.version === 3) {
            this.accountMenuDrawerService.resetDrawerPosition.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
                this.timerService.setTimeout(() => {
                    this.accountMenuDrawerService.setDrawerPosition({
                        position: DrawerPosition.Middle,
                        height: this.getNextHeight(DrawerPosition.Middle),
                        isAutomaticallyOpened: true,
                    });
                });
            });
            this.accountMenuRouter.currentRoute
                .pipe(first((p: MenuRoute | null) => p !== null))
                .subscribe(() => this.accountMenuDrawerService.resetDrawer());
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next(null);
        this.unsubscribe.complete();
    }

    onTouchStart(event: any) {
        this.startY = event.changedTouches ? event.changedTouches[0].pageY : event.deltaY;
    }

    onTouchEnd(event: any) {
        const deltaY = event.changedTouches ? event.changedTouches[0].pageY - this.startY : event.deltaY;
        const absDeltaY = Math.abs(deltaY);
        const direction = deltaY > 0 ? SwipeDirection.Down : SwipeDirection.Up;
        const swipeDirection = absDeltaY >= this.SWIPE_THRESHOLD ? direction : '';

        if (swipeDirection === SwipeDirection.Down && this.linkContainer.nativeElement.scrollTop === 0) {
            this.setNextHeight(DrawerPosition.Middle, SwipeDirection.Down);
        }
    }

    onSwipe(direction: SwipeDirection, position: DrawerPosition) {
        if (![SwipeDirection.Up, SwipeDirection.Down].includes(direction)) {
            return;
        }

        const shouldScroll = position === DrawerPosition.Middle && direction === SwipeDirection.Up;

        if (!shouldScroll) {
            this.setNextHeight(position, direction);
        }

        if (direction === SwipeDirection.Down && this.linkContainer.nativeElement.style.touchAction === 'manipulation') {
            this.setScroll(true);
        }
    }

    getIcon(position: DrawerPosition): string {
        switch (position) {
            case DrawerPosition.Bottom:
                return 'up';
            default:
                return 'down';
        }
    }

    setNextHeight(position: DrawerPosition, swipeDirection?: SwipeDirection) {
        this.accountMenuTasksService.expanded = false;
        const nextPosition = position === DrawerPosition.Bottom ? DrawerPosition.Middle : this.getNextPosition(position, swipeDirection);

        if (nextPosition === DrawerPosition.Middle) {
            this.setScroll(true);
        }

        this.timerService.setTimeout(() => {
            const height = this.getNextHeight(nextPosition);

            this.accountMenuDrawerService.setDrawerPosition({ position: nextPosition, height });
        });
    }

    private getNextHeight(position: DrawerPosition): number {
        if (position === DrawerPosition.Middle) {
            const anchor =
                this.elementRepositoryService.get(VanillaElements.ACCOUNT_MENU_TASKS_ANCHOR) ||
                this.elementRepositoryService.get(VanillaElements.ACCOUNT_MENU_SLIDER_ANCHOR);

            const anchorTopOffset = (anchor?.getBoundingClientRect().top ?? 0) + this.#window.scrollY;

            return this._doc.documentElement.clientHeight - this.scrollTop - this.scrollOffset - anchorTopOffset;
        }

        return BOTTOM_DRAWER_HEIGHT;
    }

    private onScroll(scrollTop: number) {
        this.scrollTop = scrollTop;
        this.scrollMode = scrollTop > 0;
    }

    private setScroll(isEnabled: boolean) {
        this.linkContainer.nativeElement.style.overflow = isEnabled ? 'auto' : 'hidden';
        this.linkContainer.nativeElement.style.touchAction = isEnabled ? 'manipulation' : 'none';
    }

    private getNextPosition(position: DrawerPosition, direction?: SwipeDirection): number {
        switch (direction) {
            case SwipeDirection.Up:
                return this.scrollMode ? DrawerPosition.Middle : Math.min(position + 1, DrawerPosition.Middle);
            case SwipeDirection.Down:
                return this.scrollMode || position === DrawerPosition.Bottom ? DrawerPosition.Bottom : position - 1;
            default:
                return DrawerPosition.Bottom;
        }
    }
}
